// Who is signed in, and the backend to use. Every page reads these through useSession().
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { type Api, isSignedOut } from "../lib/api";
import type { Status } from "../lib/types";
import { errorMessage } from "./ui";

export interface SessionState {
  api: Api;
  loading: boolean;
  signedIn: boolean;
  status: Status | null;
  error: string | null;
  reload: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider(
  { api, children }: { api: Api; children: ReactNode },
) {
  const [state, setState] = useState({
    loading: true,
    signedIn: false,
    status: null as Status | null,
    error: null as string | null,
  });

  const reload = useCallback(async () => {
    try {
      if (!(await api.hasSession())) {
        setState({
          loading: false,
          signedIn: false,
          status: null,
          error: null,
        });
        return;
      }
      const status = await api.status();
      setState({ loading: false, signedIn: true, status, error: null });
    } catch (e) {
      if (isSignedOut(e)) {
        setState({
          loading: false,
          signedIn: false,
          status: null,
          error: null,
        });
      } else {setState({
          loading: false,
          signedIn: true,
          status: null,
          error: errorMessage(e),
        });}
    }
  }, [api]);

  const signOut = useCallback(async () => {
    await api.signOut();
    setState({ loading: false, signedIn: false, status: null, error: null });
    window.location.hash = "#/";
  }, [api]);

  useEffect(() => {
    void reload();
    return api.onSessionChange(() => void reload());
  }, [api, reload]);

  return (
    <SessionContext.Provider value={{ api, ...state, reload, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSession outside SessionProvider");
  return s;
}

/** The signed-in member of an active organization (pages behind the org gate only). */
export function useMember() {
  const { status, api } = useSession();
  const member = status?.member;
  if (!member) throw new Error("useMember outside the organization pages");
  return { api, member, tz: member.timezone };
}
